import type * as v0 from "@aidkitorg/types/lib/survey";
import { useLocalizedStrings } from "../Localization";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import InterfaceContext, { AuthContext } from "../Context";
import { ClickableButton } from "../Components/Button";
import { usePost } from "../API";
import { useToast } from "@aidkitorg/component-library";
import { PaperAirplaneIcon, ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { SpacedSpinner, prettyPhone, safeParse } from "../Util";
import { SupportedLanguage } from "@aidkitorg/i18n/lib";
import type { LinkInitiation } from "@aidkitorg/aidkit/lib/application/liveness";
import { QuestionProps } from "./Props";

export const LIVENESS_REQUEST_EVENT_TYPE = 'LIVENESS_REQUEST';

export function LivenessDetectionQuestion(props: { 
  component: v0.LivenessDetection,
  uid: string,
  info: Record<string, string | undefined>,
  setInfo: (key: string, value: string) => void,
  questionProps: QuestionProps
}) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const { toast } = useToast();

  const content = useModularMarkdown({
    content: props.component.content,
    info: props.info
  });

  const doneContent = useModularMarkdown({
    content: props.component.completionContent || "&#127881; **" + L.questions.identity.identity_verification_passed + "** &#127881;",
    info: props.info
  })
  
  type Initiation = LinkInitiation | {
    kind: "new_window"
  }; 

  const modes: Record<string, Initiation> = {
    newWindow: {
      kind: "new_window"
    },
    linkPreferred: {
      kind: "link",
      contactMethod: "preferred"
    },
    linkSms: {
      kind: "link",
      contactMethod: "sms"
    },
    linkEmail: {
      kind: "link",
      contactMethod: "email"
    },
    ...(props.component.contactFields || []).reduce((acc, cur) => {
      acc['linkAlt' + cur] = {
        kind: "link_alt",
        targetField: cur
      }
      return acc;
    }, {} as Record<string, Initiation>)
  }

  /** 
   * Liveness Session Mode Configuration
   * - options: Open in new tab, Send link, or Scan QR Code (TBD)  
   **/
  const [linkSent, setLinkSent] = useState(null as null | "email" | "sms");
  const [linkSentTo, setLinkSentTo] = useState('');
  const [modeDecided, setModeDecided] = useState<null | keyof typeof modes>(null);
  const [windowOpened, setWindowOpened] = useState<Window | null>(null);
  const [allowDesktopWebcam, setAllowDesktopWebcam] = useState(false);
  const [allowNewWindow, setAllowNewWindow] = useState<boolean>((window.innerHeight > window.innerWidth) ? true : false);

  useEffect(() => {
    (async () => {
      // only allow desktop webcam if they actually have one
      if (props.component.allowDesktopWebcam) {
        const devices = await navigator?.mediaDevices?.enumerateDevices();
        setAllowDesktopWebcam(devices?.some(d => d.kind === 'videoinput'));
      }
    })();
  }, [props.component.allowDesktopWebcam]);

  const sendLivenessLink = usePost("/applicant/send_liveness_link");
  const getLivenessData = usePost("/applicant/get_liveness_data");
  const initiateLivenessSession = usePost("/applicant/initiate_liveness_session");
  const completeLivenessSession = usePost("/applicant/complete_liveness_session");

  const [initiatingSession, setInitiatingSession] = useState(false);

  // Handle available modes based on desktop webcam and resize events
  useEffect(() => {
    if (allowDesktopWebcam) {
      setAllowNewWindow(true);
    }

    const onResizeSetMode = () => {
      if (allowDesktopWebcam) return;
      if (window.innerHeight > window.innerWidth) {
        setAllowNewWindow(true);
      } else {
        setAllowNewWindow(false);
      }
    }

    window.addEventListener('resize', onResizeSetMode);
    return () => { 
      window.removeEventListener('resize', onResizeSetMode);
    }
  }, [allowDesktopWebcam, document.visibilityState]);

  useEffect(() => {
    const cb = async () => {
      // Only check liveness data IF it's curreltny PENDING AND we are not in a VIEW ONLY state
      if (props.questionProps.blockQuestionEdits) {
        console.log("[LivenessDetection] Skipping check - question edits blocked");
        return;
      }
      if (!(props.info[props.component.targetField] || '').startsWith('pending')) {
        console.log("[LivenessDetection] Skipping check - not pending:", props.info[props.component.targetField]);
        return;
      }
      console.log("[LivenessDetection] Checking liveness status for", props.uid, "field:", props.component.targetField);
      const resp = await getLivenessData({ applicantUid: props.uid, surveyNodeId: props.component.id });
      if(!resp) {
        // API unavailable, wait until next interval
        return;
      }

      console.log("[LivenessDetection] Got response:", resp);
      
      if (resp.kind !== 'ok' && resp?.stage === 'done' && resp.updates) {
        console.log("[LivenessDetection] Applying updates:", resp.updates);
        for (let key in resp.updates) {
          console.log("[LivenessDetection] Setting info for", key, "to", resp.updates[key]);
          props.setInfo(key, resp.updates[key] || '');
        }
        console.log("[LivenessDetection] Completing liveness session");
        await completeLivenessSession({ applicantUid: props.uid, surveyNodeId: props.component.id });
        console.log("[LivenessDetection] Session completed");
      } else {
        console.log("[LivenessDetection] Not applying updates - conditions not met:", {
          kind: resp.kind,
          stage: resp.kind === 'ok' ? undefined : resp.stage,
          hasUpdates: resp.kind === 'ok' ? false : !!resp.updates
        });
      }
    }

    const interval = setInterval(cb, 2500);
    return () => { clearInterval(interval); }
  }, [props.uid, props.info, props.component.targetField]);

  // Language management for starting the session
  const lang = useRef(props.info.language || context.lang);
  useEffect(() => {
    lang.current = props.info.language || context.lang;
  }, [props.info.language, context.lang]);

  useEffect(() => {
    if (!windowOpened || windowOpened.closed || !props.info?.[props.component.targetField]?.includes('pending')) return;

    const startLivenessSession = async () => {
      if (props.questionProps.blockQuestionEdits) return;

      console.log("Starting liveness session");
      setInitiatingSession(true);
      const resp = await initiateLivenessSession({ 
        surveyNodeId: props.component.id, 
        lang: lang.current as SupportedLanguage, 
        applicantUid: props.uid 
      });
      if((resp as any)?.error){
        windowOpened.close();
      }
      else if (resp?.link && windowOpened) {
        windowOpened.location = resp.link;
      }
      setInitiatingSession(false);
    }
    startLivenessSession();

    function onRequestNewLivenessURL(e:MessageEvent<string>){
      // ensure this event is coming from us
      if (e.origin !== window.location.origin) {
        return;
      }
      const { type } = typeof e.data === 'object' ? e.data : safeParse(e.data);
      // restart the liveness session
      if (type === LIVENESS_REQUEST_EVENT_TYPE) {
        startLivenessSession();
      }
    }

    let interval = setInterval(async () => { 
      if (windowOpened.closed) {
        setWindowOpened(null);
      }
    }, 1000);
    // listen for postMessage events from liveness page requesting a new URL
    window.addEventListener('message', onRequestNewLivenessURL);
    return () => {
      clearInterval(interval);
      window.removeEventListener('message', onRequestNewLivenessURL);
    }
  }, [windowOpened, props.component.id, props.component.targetField, props.uid, props.info?.[props.component.targetField]]);

  const blur = ((contact: string) => {
    if (contact.includes('@')) {
      // email, blur everything before the @ except very first character
      return contact.charAt(0) + contact.slice(1).replace(/[^@](?=.*@)/g, '*');
    } else {
      // prettify and remove all chars except digits and dashes
      return prettyPhone(contact).replace(/\s/,'-').replace(/[^0-9\-]/g, '')
        .replace(/\d(?=(?:\D*\d){2,})/g, '*'); // blur everything except last 2 digits and dashes
    }
  })

  const selectOptions = useMemo(() => [...props.info.phone_number ? [{
    value: "linkSms",
    label: L.selfie.send_link_sms.replace('{}', blur(props.info.phone_number || '')),
  }] : [],
  ...props.info.email ? [{
    value: "linkEmail",
    label: L.selfie.send_link_email.replace('{}', blur(props.info.email || '')), 
  }] : [],
  ...(props.component.contactFields || []).filter(f => f !== 'phone_number' && f !== 'email' && !!props.info[f]).map(field => {
    // console.log("Field",field,"props",props.info[field], blur(props.info[field] || ''));
    return {
      value: "linkAlt" + field,
      label: L.selfie.send_link_alt.replace('{}', blur(props.info[field] || '')),
    }
  }),
  ...allowNewWindow ? [{
    value: "newWindow",
    label: L.selfie.open_a_new_tab_option
  }] : []], [allowNewWindow, props.component.contactFields, props.info]);

  return <>
    <fieldset>
      {props.info[props.component.targetField] && !props.info[props.component.targetField]!.includes('pending') ? 
        <>
          <legend>{doneContent}</legend>
        </> : 
        <>
          <legend>
            {content}
          </legend>
          
          {(linkSent || windowOpened) ? 
            <div className="rounded-md bg-blue-50 p-2 items-center">
              <div className="flex">
                <div className="flex-shrink-0">
                  {linkSent 
                    ? <PaperAirplaneIcon className="h-8 text-blue-300" />
                    : <ArrowsRightLeftIcon className="h-8 text-blue-300" />}
                </div>
                <div className="ml-2 items-center">
                  <h3 className="text-sm font-medium text-blue-800">
                    {(linkSent && linkSentTo)
                      ? (linkSent === 'email' 
                        ? <span>{L.applicant.link_sent_check_your_email.replace('{}',linkSentTo)}</span> 
                        : <span>{L.applicant.link_sent_check_your_phone.replace('{}', prettyPhone(linkSentTo))}</span>)
                      : <span>{L.selfie.we_opened_a_new_tab}</span>}
                  </h3>
                </div>
              </div>
            </div> : null}

          {!windowOpened && !linkSent && (
            <div className="mb-2">
              <label htmlFor="modedecider" className="block text-sm font-medium leading-6">
                {allowDesktopWebcam
                  ? <span>{L.selfie.how_would_you_like_to_complete}</span> 
                  : <span>{L.selfie.identity_verification_requires_a_phone}</span>}
              </label>
              <select 
                id="modedecider"
                className="mr-2 mt-1 shadow-sm block w-full overflow-hidden pl-3 bg-white pr-2 py-2 text-black text-base border-2 border-gray-200 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" 
                onChange={(e) => {
                  setModeDecided(e.target.value);
                }}
                defaultValue={modeDecided ?? ""} >
                <option value="" disabled>{L.selfie.click_here_to_choose}</option>
                {selectOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
                {/** placeholder for Scanning a QR Code option */}
              </select>
            </div>)}

          {!props.questionProps.blockQuestionEdits && !linkSent && (!allowNewWindow || modeDecided?.startsWith("link")) &&
            <div className="flex-col">
              <ClickableButton color="indigo" onClick={async (e) => {
                e.preventDefault();
                setInitiatingSession(true);
                const resp = await sendLivenessLink({ 
                  surveyNodeId: props.component.id, 
                  lang: props.info?.language as SupportedLanguage || context.lang,
                  applicantUid: props.uid,
                  initiationMode: modes[modeDecided as keyof typeof modes] as LinkInitiation,
                  ...(props.component.contactFields?.length ? { 
                    alternateContacts: [...props.component.contactFields || []].reduce((acc,cur) => {
                      acc[cur] = props.info[cur];
                      return acc;
                    }, {} as { [field: string]: string | undefined })
                  } : {})
                });
                if (resp?.ok) {
                  toast({
                    description: (resp.ok === 'email' ? L.applicant.link_sent_check_your_email : L.applicant.link_sent_check_your_phone).replace('{}', resp.contact),
                    variant: 'success'
                  });
                  setLinkSent(resp.ok);
                  setLinkSentTo(resp.contact);
                  setWindowOpened(null);
                }
                setInitiatingSession(false);
                props.setInfo(props.component.targetField, 'pending:' + resp.ok);
              }}>
                {initiatingSession && <SpacedSpinner />}<span>{allowDesktopWebcam ? <span>{L.selfie.begin}</span> : <span>{L.selfie.send_me_a_link}</span>}</span>
              </ClickableButton>
            </div>}
           
          {!props.questionProps.blockQuestionEdits && !windowOpened && modeDecided === 'newWindow' && <div className="flex-col">
            <ClickableButton color="indigo" onClick={async (e) => {
              e.preventDefault();
              props.setInfo(props.component.targetField, 'pending:window');
              setWindowOpened(window.open("", "_blank"));
            }}>
              {initiatingSession && <SpacedSpinner />}<span>
                {allowDesktopWebcam ? <span>{L.selfie.begin}</span> : <span>{L.selfie.open_a_new_tab}</span>}
              </span>
            </ClickableButton>
          </div>
          }
          
        </>}
    </fieldset>
  </>
}
